import React, { useState, useEffect } from 'react';
import axiosInstance from '../axiosConfig';
import { isSmartCard } from '../smart/model';
import './Page.css';
import { Cookies } from 'react-cookie'; 
import { useNavigate, Link } from 'react-router-dom';
import {
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  CssBaseline,
  Stack,
  Card,
  CardContent,
  CardActions,
  Divider,
  Fade,
  Container,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import InsightsIcon from '@mui/icons-material/Insights';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { styled } from '@mui/material/styles';
import AppTheme from './shared-theme/AppTheme';
import ColorModeSelect from './shared-theme/ColorModeSelect';

const ProfileContainer = styled(Stack)(({ theme }) => ({
  minHeight: '100dvh',
  position: 'relative',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'fixed',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  minHeight: '100dvh',
  width: '100%',
  position: 'relative',
  padding: 0,
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
  [theme.breakpoints.up('sm')]: {
    padding: 0,
  },
}));

const StoreCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  background: theme.palette.background.paper,
  boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: 'rgba(0, 0, 0, 0.1) 0px 10px 30px 0px',
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
}));

const InsightButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(0.5, 2),
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.primary.lighter || 'rgba(33, 150, 243, 0.08)',
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
  },
  gap: theme.spacing(0),
}));

const AddStoreButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  textTransform: 'none',
  padding: theme.spacing(1.5),
  fontWeight: 600,
  fontSize: '1rem',
  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
  boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
  marginBottom: theme.spacing(3),
  '&:hover': {
    background: 'linear-gradient(45deg, #1976D2 30%, #00B4D8 90%)',
    boxShadow: '0 4px 8px 2px rgba(33, 203, 243, .4)',
  },
}));

const LogoutButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  textTransform: 'none',
  padding: theme.spacing(1, 1),
  fontWeight: 600,
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

const DeleteDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
  },
  '& .MuiDialogTitle-root': {
    fontSize: '1.25rem',
    fontWeight: 400,
  },
}));

const DeleteActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 400,
  borderRadius: theme.spacing(3),
  padding: theme.spacing(1, 3),
  '&.delete': {
    backgroundColor: '#d32f2f',
    color: '#fff'
  },
}));

function ProfilePage() {
  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await axiosInstance.get('/auth/check-session');
        console.log('response from check-session', response)
        if (!response.data.userId) {
          // Redirect to login if no userId in session
          navigate('/login');
        }
      } catch (error) {
        console.error('Error checking session:', error);
        // Handle error (e.g., redirect to login or display an error message)
        navigate('/login');
      }
    };
    checkSession();
  }, [navigate]); // Include navigate in the dependency array

  const [profileData, setProfileData] = useState({
    email: '',
    businesses: []
  });

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    businessId: null,
    businessName: ''
  });

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await axiosInstance.get('/profile');
        setProfileData(response.data);
      } catch (error) {
        console.error('Error fetching profile:', error);
        // Handle error (e.g., display an error message)
      }
    };

    fetchProfileData();
  }, []);

  const handleDeleteClick = (business) => {
    setDeleteDialog({
      open: true,
      businessId: business._id,
      businessName: business.businessName
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      businessId: null,
      businessName: ''
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.businessId) return;

    try {
      await axiosInstance.delete(`/delete-qr-code/${deleteDialog.businessId}`);

      // Update the profileData after successful deletion
      setProfileData((prevData) => ({
        ...prevData,
        businesses: prevData.businesses.filter(
          (business) => business._id !== deleteDialog.businessId
        ),
      }));

      // Close the dialog
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Error deleting business:", error);
      // Handle error (e.g., display an error message)
    }
  };

  const handleReviewInsight = (qrCodeId, placeId) => {
    navigate(`/review-insight/${qrCodeId}`, {
      state: { placeId }
    }); // Include placeId in the URL
  };

  const handleLogout = async () => {
    try {
        
      await axiosInstance.post('/auth/logout');
      const cookies = new Cookies();
      cookies.remove('connect.sid');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Handle error
    }
  };

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <ProfileContainer>
        <StyledPaper>
          <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <ColorModeSelect />
            </Box>
            <Fade in timeout={800}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {/* Title */}
                <Typography
                  variant="h5"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 600,
                    mb: 3,
                  }}
                >
                  <StorefrontIcon color="primary" sx={{ fontSize: 28 }} />
                  Your Stores
                </Typography>

                {/* Add Store Button */}
                {profileData.businesses.some(business => isSmartCard(business._id, business.qrType)) && <Button component={Link} to="/studio"
                  variant="outlined" sx={{ mb: 2 }}>Customize cards &amp; stands ↗</Button>}
                <AddStoreButton
                  variant="contained"
                  component={Link}
                  to="/add-qr-code"
                  startIcon={<AddCircleIcon />}
                  fullWidth
                >
                  Add Store
                </AddStoreButton>

                {/* Stores List */}
                <Stack spacing={2} sx={{ flex: 1, mb: 4 }}>
                  {profileData.businesses.filter(business => business.qrType !== 'smart').map((business) => (
                    <Fade in key={business._id} timeout={500}>
                      <StoreCard elevation={0} sx={{ paddingBottom: 0.5 }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom fontWeight="600">
                            {business.businessName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {business.businessAddress}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
                            <strong>Stand Name:</strong> {business.qrCodeName}
                          </Typography>
                          
                        </CardContent>
                        <Divider />
                        <CardActions sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          px: 0,
                          py: 1
                        }}>
                          {isSmartCard(business._id, business.qrType)
                            ? <Button component={Link} to={`/studio/${business._id}`}>Customize page</Button>
                            : <InsightButton
                            onClick={() => handleReviewInsight(business._id, business.businessPlaceId)}
                            startIcon={<InsightsIcon />}
                          >
                            Review Insights
                          </InsightButton>}
                          <Tooltip title="Delete Store">
                            <DeleteButton
                              size="medium"
                              onClick={() => handleDeleteClick(business)}
                            >
                              <DeleteIcon />
                            </DeleteButton>
                          </Tooltip>
                        </CardActions>
                      </StoreCard>
                    </Fade>
                  ))}
                </Stack>

                {/* Logout Button */}
                <Box sx={{ mt: 'auto' }}>
                  <LogoutButton
                    variant="text"
                    onClick={handleLogout}
                    startIcon={<LogoutIcon />}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Logout
                  </LogoutButton>
                </Box>

                {/* Delete Confirmation Dialog */}
                <DeleteDialog
                  open={deleteDialog.open}
                  onClose={handleCloseDeleteDialog}
                  aria-labelledby="delete-dialog-title"
                >
                  <DialogTitle id="delete-dialog-title">
                    Delete Store
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      Are you sure you want to delete <strong>{deleteDialog.businessName}</strong>?
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions sx={{ padding: 1, gap: 1 }}>
                    <DeleteActionButton
                      onClick={handleCloseDeleteDialog}
                      variant="outlined"
                    >
                      Cancel
                    </DeleteActionButton>
                    <DeleteActionButton
                      onClick={handleConfirmDelete}
                      className="delete"
                      variant="contained"
                    >
                      Delete Store
                    </DeleteActionButton>
                  </DialogActions>
                </DeleteDialog>
              </Box>
            </Fade>
          </Container>
        </StyledPaper>
      </ProfileContainer>
    </AppTheme>
  );
}

export default ProfilePage;
